import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

export interface ProductDetailsSpecificationStyles {
    wrapper?: InjectableCss;
    specificationTittle?: InjectableCss;
    tablediv?: InjectableCss;
    dataRowCss?: InjectableCss;
    descriptionAccordion?: InjectableCss;
    tableContainer?: InjectableCss;
    firstTableDiv?: InjectableCss;
    tableBody?: InjectableCss;
    specificationLable?: InjectableCss;
    attributeValues?: InjectableCss;
}

export const attributesStyles: ProductDetailsSpecificationStyles = {
    wrapper: {
        css: css`
            margin: 20px 0px;
        `,
    },
    specificationTittle: {
        css: css`
            border-bottom: 0.2rem solid #fc3;
            box-sizing: border-box;
            display: inline-flex;
            font-weight: 600;
            color: #474747;
            font-size: 24px;
            text-transform: uppercase;
        `,
    },
    tablediv: {
        css: css`
            border-collapse: collapse;
            border-spacing: 0;
            width: 100%;
        `,
    },
    dataRowCss: {
        css: css`
            width: 100%;
            height: auto;
            display: flex;
            border: 1px solid #acacac;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
            :nth-child(even) {
                background-color: white !important;
            }
        `,
    },
    descriptionAccordion: {
        css: css`
            margin-bottom: 20px;
            border-top: 1px solid #ccc;
        `,
    },
    tableContainer: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-left: 0;
            margin-right: 0;
        `,
    },
    firstTableDiv: {
        css: css`
            flex: 0 0 50%;
            max-width: 50%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 0 0 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex: 0 0 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex: 0 0 100%;
                        max-width: 100%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tableBody: {
        css: css`
            border: 0 solid;
            border-color: inherit;
            display: flex;
            flex-wrap: wrap;
            flex-direction: column;
        `,
    },
    specificationLable: {
        css: css`
            width: 50%;
            padding: 5px;
            background: #ccc;
            font-weight: 700;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 40%;
                    `,
                    css`
                        width: 40%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    attributeValues: {
        css: css`
            width: 50%;
            color: #000;
            padding: 0px 10px;
            white-space: normal;
            display: flex;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 60%;
                    `,
                    css`
                        width: 60%;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = attributesStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasParentProductId & HasProduct;

const mapStateToProps = (state: ApplicationState, ownProps: HasParentProductId) => ({
    variantProductState: getProductState(state, state.pages.productDetails.selectedProductId),
});

const ProductDetailsSpecification: React.FC<Props> = ({ product, variantProductState, fields }) => {
    if (!product || !product.properties) {
        return null;
    }

    const [specification, setSpecification] = React.useState([]);

    React.useEffect(() => {
        if (product && product.properties.skU_ADDITIONAL_ATTRIBUTES) {
            setSpecification(JSON.parse(product.properties.skU_ADDITIONAL_ATTRIBUTES));
        }
    }, [product]);
    React.useEffect(() => {
        if (variantProductState.value && variantProductState.value.properties.skU_ADDITIONAL_ATTRIBUTES) {
            setSpecification(JSON.parse(variantProductState.value.properties.skU_ADDITIONAL_ATTRIBUTES));
        }
    }, [variantProductState]);

    if (specification.length === 0) {
        return null;
    }

    const middleIndex = Math.ceil(specification.length / 2);
    const leftColumn = specification.slice(0, middleIndex);
    const rightColumn = specification.slice(middleIndex);

    return (
        <>
            {specification.length > 0 && (
                <StyledWrapper {...styles.wrapper} data-test-selector="productDetails_specification">
                    <StyledWrapper {...styles.descriptionAccordion}>
                        <StyledWrapper>
                            <Typography {...styles.specificationTittle}>
                                {fields.title ? fields.title : "SPECIFICATIONS"}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.tableContainer}>
                            <StyledWrapper {...styles.firstTableDiv}>
                                <DataTable {...styles.tablediv}>
                                    <DataTableBody {...styles.tableBody}>
                                        {leftColumn?.map((specification: any, index: number) => (
                                            <DataTableRow {...styles.dataRowCss} key={index}>
                                                <Typography {...styles.specificationLable}>
                                                    {specification.AttributeDisplayName}
                                                </Typography>
                                                <Typography {...styles.attributeValues}>
                                                    {specification.AttributeValue}
                                                </Typography>
                                            </DataTableRow>
                                        ))}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                            <StyledWrapper {...styles.firstTableDiv}>
                                <DataTable {...styles.tablediv}>
                                    <DataTableBody {...styles.tableBody}>
                                        {rightColumn?.map((specification: any, index: number) => (
                                            <DataTableRow {...styles.dataRowCss} key={index}>
                                                <Typography {...styles.specificationLable}>
                                                    {specification.AttributeDisplayName}
                                                </Typography>
                                                <Typography {...styles.attributeValues}>
                                                    {specification.AttributeValue}
                                                </Typography>
                                            </DataTableRow>
                                        ))}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsSpecification)),
    definition: {
        displayName: "Custom Specification",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title ",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
        ],
    },
};

export default widgetModule;
